<template>
  <!--
    CRIAR O CLÃ, em papel — e com a REGRA no lugar do sabor (D22/7.4c): o tipo É a política de
    entrada. Cooperativa = qualquer um entra direto; Corporação = só aceito ou convidado. A tela
    antiga oferecia "aberto/só por convite" num campo que o cliente jogava fora; agora a escolha
    que aparece é a que vale.
  -->
  <FishModal :aberto="true" :titulo="$t('clan.createClan')" @fechar="$emit('close')">
    <form class="criar" @submit.prevent="criar">
      <label class="criar__campo">
        <span class="t-legenda">{{ $t('clan.nameLabel') }}</span>
        <input v-model.trim="nome" type="text" maxlength="40" :placeholder="$t('clan.namePlaceholder')">
      </label>

      <label class="criar__campo">
        <span class="t-legenda">{{ $t('clan.descriptionLabel') }}</span>
        <textarea v-model.trim="descricao" rows="2" maxlength="200" :placeholder="$t('clan.descriptionPlaceholder')"></textarea>
      </label>

      <!--
        O PAÍS (11/09/2026) — de onde o clã é. Aparece ao lado do nome na lista, no quadro de honra
        e no cais. Vem marcado com o país do navegador, e "Sem país" é uma escolha que vale.
      -->
      <div class="criar__campo">
        <span class="t-legenda">{{ $t('clan.pais.rotulo') }}</span>
        <EscolhaDePais v-model="pais" />
        <span class="criar__dica">{{ $t('clan.pais.dica') }}</span>
      </div>

      <fieldset class="criar__tipos">
        <legend class="t-legenda">{{ $t('clan.typeLabel') }}</legend>
        <button
          v-for="opcao in tipos"
          :key="opcao.valor"
          type="button"
          class="criar__tipo"
          :class="{ 'criar__tipo--escolhido': tipo === opcao.valor }"
          @click="tipo = opcao.valor"
        >
          <FishIcon :nome="opcao.icone" tamanho="pequeno" />
          <span class="criar__tipo-nome">{{ $t(`clan.types.${opcao.valor}`) }}</span>
          <span class="criar__tipo-regra">{{ $t(`clan.entryRule.${opcao.valor}`) }}</span>
        </button>
      </fieldset>

      <p class="criar__custo">
        <FishIcon nome="coins" tamanho="miudo" />
        {{ $t('clan.creationCost', { cost: custoFormatado }) }}
      </p>
    </form>

    <template #acoes>
      <FishButton
        variante="primario"
        bloco
        :desabilitado="!nome"
        :motivo="nome ? '' : $t('clan.namePlaceholder')"
        som="compra"
        @click="criar"
      >
        {{ $t('clan.createButton') }}
      </FishButton>
    </template>
  </FishModal>
</template>

<script>
import { FishModal, FishButton, FishIcon } from '@/shared/ui';
import EscolhaDePais from '@/shared/paises/EscolhaDePais.vue';
import { paisDoNavegador } from '@/shared/paises/paises';

/**
 * O que a API cobra para fundar (`CUSTO_PARA_CRIAR`, em `apps/api/src/clan/clan.service.ts`).
 * Só aparece na frase do custo: quem debita é o servidor, e nenhuma rota devolve o valor. Se ele
 * mudar lá, muda aqui — a divergência custa uma frase errada, nunca ouro errado.
 */
const CUSTO_PARA_CRIAR = 10000;

export default {
  name: 'CreateClanModal',
  components: { FishModal, FishButton, FishIcon, EscolhaDePais },
  emits: ['close', 'create'],
  data() {
    return {
      nome: '',
      descricao: '',
      pais: paisDoNavegador(),
      tipo: 'cooperative',
      tipos: [
        { valor: 'cooperative', icone: 'globe' },
        { valor: 'corporation', icone: 'lock' },
      ],
    };
  },
  computed: {
    custoFormatado() {
      return new Intl.NumberFormat(this.$i18n.locale).format(CUSTO_PARA_CRIAR);
    },
  },
  methods: {
    criar() {
      if (!this.nome) return;
      this.$emit('create', {
        name: this.nome,
        description: this.descricao,
        type: this.tipo,
        country: this.pais,
      });
    },
  },
};
</script>

<style scoped>
.criar {
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

.criar__campo {
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  color: var(--tinta-fraca);
}

.criar__campo input,
.criar__campo textarea {
  border: 2px solid var(--madeira);
  background: var(--papel-frio);
  padding: var(--e-2);
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  color: var(--tinta);
  resize: vertical;
}

.criar__campo input:focus,
.criar__campo textarea:focus {
  outline: 2px solid var(--latao);
  outline-offset: -2px;
}

.criar__dica {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.criar__tipos {
  border: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--e-2);
}

.criar__tipos legend {
  color: var(--tinta-fraca);
  margin-bottom: var(--e-1);
}

/* A escolha do tipo mostra A REGRA, não dois parágrafos de sabor (D22). */
.criar__tipo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-1);
  padding: var(--e-2);
  border: 2px solid var(--madeira);
  background: var(--papel-frio);
  color: var(--tinta);
  cursor: pointer;
  font-family: var(--fonte-corpo);
  text-align: center;
  transition: transform var(--t-rapido) var(--curva-quique);
}

@media (hover: hover) {
  .criar__tipo:hover {
    transform: translateY(-2px);
  }
}

.criar__tipo--escolhido {
  border-color: var(--latao);
  background: var(--papel);
  box-shadow: 0 0 0 2px var(--latao);
}

.criar__tipo-nome {
  font-family: var(--fonte-display);
  font-size: var(--f-2);
}

.criar__tipo-regra {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.criar__custo {
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

@media (max-width: 480px) {
  .criar__tipos {
    grid-template-columns: 1fr;
  }
}
</style>
